import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  FloatingFocusManager,
  size,
  safePolygon,
  type Placement,
  type UseFloatingReturn,
  type ReferenceType,
} from "@floating-ui/react";

export const openManagementModesEnum = {
  CLICK: "CLICK",
  HOVER: "HOVER",
} as const;

export type OpenManagementMode =
  (typeof openManagementModesEnum)[keyof typeof openManagementModesEnum];

const DEFAULT_FALLBACK_PLACEMENTS: Placement[] = [
  "bottom",
  "top",
  "bottom-start",
  "bottom-end",
  "top-start",
  "top-end",
];

export interface UsePopoverOptions {
  placement?: Placement;
  modal?: boolean;
  open?: boolean | null;
  onOpenChange?: (open: boolean) => void;
  useSameWidth?: boolean;
  onOuterAction?: () => void;
  openManagementMode?: OpenManagementMode | null;
  virtualReference?: ReferenceType | null;
  delayOpen?: number;
  delayClose?: number;
  fallbackPlacements?: Placement[];
}

export type UsePopoverReturn = UseFloatingReturn &
  ReturnType<typeof useInteractions> & {
    open: boolean;
    setOpen: (open: boolean) => void;
    modal?: boolean;
  };

export const usePopover = ({
  placement = "bottom",
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  useSameWidth = false,
  onOuterAction,
  openManagementMode = openManagementModesEnum.CLICK,
  virtualReference = null,
  delayOpen = 0,
  delayClose = 0,
  fallbackPlacements = DEFAULT_FALLBACK_PLACEMENTS,
}: UsePopoverOptions = {}): UsePopoverReturn => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  const isControlled = controlledOpen !== null && controlledOpen !== undefined;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    elements: virtualReference ? { reference: virtualReference } : undefined,
    middleware: [
      offset(5),
      flip({
        fallbackStrategy: "bestFit",
        fallbackPlacements,
      }),
      shift({
        padding: {
          top: 65,
          bottom: 10,
          left: 10,
          right: 10,
        },
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          if (useSameWidth) {
            Object.assign(elements.floating.style, {
              maxHeight: `${availableHeight}px`,
              width: `${rects?.reference.width}px`,
            });
          }
        },
        padding: {
          top: 65,
          bottom: 10,
          left: 10,
          right: 10,
        },
      }),
    ],
  });

  const context = data.context;

  const outsidePressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(
    () => () => {
      if (outsidePressTimeoutRef.current !== null) {
        clearTimeout(outsidePressTimeoutRef.current);
        outsidePressTimeoutRef.current = null;
      }
    },
    []
  );

  const handleOutsidePress = useCallback(() => {
    if (outsidePressTimeoutRef.current !== null) {
      clearTimeout(outsidePressTimeoutRef.current);
    }
    outsidePressTimeoutRef.current = setTimeout(() => {
      outsidePressTimeoutRef.current = null;
      if (onOuterAction) {
        onOuterAction();
      } else {
        setOpen(false);
      }
    }, 200);

    return true;
  }, [onOuterAction, setOpen]);

  const click = useClick(context, {
    enabled:
      !virtualReference &&
      !isControlled &&
      openManagementMode === openManagementModesEnum.CLICK,
  });
  const hover = useHover(context, {
    move: false,
    enabled:
      !virtualReference &&
      !isControlled &&
      openManagementMode === openManagementModesEnum.HOVER,
    delay: {
      open: delayOpen,
      close: delayClose,
    },
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context, {
    outsidePress: handleOutsidePress,
  });
  const role = useRole(context);

  const interactions = useInteractions([click, hover, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
    }),
    [open, setOpen, interactions, data, modal]
  );
};

const PopoverContext = React.createContext<UsePopoverReturn | null>(null);

export const usePopoverContext = (): UsePopoverReturn => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error("Popover components must be wrapped in <Popover />");
  }

  return context;
};

export interface PopoverProps extends UsePopoverOptions {
  children: React.ReactNode;
  // Accepted for API compatibility (SeemPopover forwards it); unused at runtime, matching main's JS.
  className?: string;
}

export const Popover: React.FC<PopoverProps> = ({
  children,
  modal = false,
  ...restOptions
}) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({ modal, ...restOptions });
  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
};

export interface PopoverTriggerProps extends React.HTMLProps<HTMLDivElement> {
  children: React.ReactNode;
  asChild?: boolean;
  triggerFullWidth?: boolean;
}

export const PopoverTrigger = forwardRef<HTMLDivElement, PopoverTriggerProps>(
  function PopoverTrigger(
    { children, asChild = false, className, triggerFullWidth = false, ...props },
    propRef
  ) {
    const context = usePopoverContext();
    const childrenRef = (
      children as React.ReactElement & { ref?: React.Ref<unknown> }
    ).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children,
        context.getReferenceProps({
          ref,
          ...props,
          ...children.props,
          style: triggerFullWidth ? { width: "100%" } : {},
          "data-state": context.open ? "open" : "closed",
        })
      );
    }

    return (
      <div
        className={`popover-trigger-wrap ${className || ""}`}
        ref={ref}
        style={triggerFullWidth ? { width: "100%" } : {}}
        // The user can style the trigger based on the state
        data-state={context.open ? "open" : "closed"}
        {...context.getReferenceProps(props)}
      >
        {children}
      </div>
    );
  }
);

export type PopoverContentProps = React.HTMLProps<HTMLDivElement>;

export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent(props, propRef) {
    const { context: floatingContext, ...context } = usePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    // Preserve original runtime check: tests `.getAttribute` directly on the
    // reference ref object (not its `.current`).
    const reference = context.refs.reference as unknown as {
      getAttribute?: unknown;
    } | null;
    const hasDomReference =
      reference && typeof reference.getAttribute === "function";

    if (!floatingContext.open) {
      return null;
    }

    const content = (
      <div
        className={"popover-trigger-inner"}
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...props.style,
        }}
        {...context.getFloatingProps(props)}
      >
        {props.children}
      </div>
    );

    return (
      <FloatingPortal>
        {hasDomReference ? (
          <FloatingFocusManager
            context={floatingContext}
            modal={context.modal}
            initialFocus={-1}
            returnFocus={false}
          >
            {content}
          </FloatingFocusManager>
        ) : (
          content
        )}
      </FloatingPortal>
    );
  }
);
